'use client';

import { useCallback, useMemo } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@kit/ui/dropdown-menu';
import { cn } from '@kit/ui/utils';

import { setLocaleAction } from '~/lib/i18n/set-locale';

/**
 * Language picker for the account dropdown, mirroring `SubMenuModeToggle`:
 * a submenu on `lg`+ and a flat labelled list below (submenus are unusable on
 * touch). Routes every change through the single writer in set-locale.ts.
 * `selectedLocale` is the explicit cookie choice read by the server parent;
 * `null` = Automatic.
 */
export function LanguageSubMenu({
  selectedLocale,
}: {
  selectedLocale: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { language: currentLanguage, options } = i18n;

  const locales = (options.supportedLngs as string[]).filter(
    (locale) => locale.toLowerCase() !== 'cimode',
  );

  const languageNames = useMemo(() => {
    return new Intl.DisplayNames([currentLanguage], { type: 'language' });
  }, [currentLanguage]);

  const languageChanged = useCallback(
    async (locale: string | null) => {
      await setLocaleAction({ locale });

      if (locale) {
        await i18n.changeLanguage(locale);
      }

      // Server components rendered in the previous locale must re-render.
      window.location.reload();
    },
    [i18n],
  );

  const MenuItems = (
    <>
      <DropdownMenuItem
        className={cn('flex cursor-pointer items-center space-x-2', {
          'bg-muted': selectedLocale === null,
        })}
        onClick={() => languageChanged(null)}
      >
        <span>{t('common:language.automatic')}</span>
      </DropdownMenuItem>

      {locales.map((locale) => (
        <DropdownMenuItem
          className={cn('flex cursor-pointer items-center space-x-2', {
            'bg-muted': selectedLocale === locale,
          })}
          key={locale}
          onClick={() => languageChanged(locale)}
        >
          <span>{capitalize(languageNames.of(locale) ?? locale)}</span>
        </DropdownMenuItem>
      ))}
    </>
  );

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          className={
            'hidden w-full items-center justify-between gap-x-3 lg:flex'
          }
        >
          <span className={'flex items-center space-x-2'}>
            <Globe className={'h-4'} />

            <span>{t('common:language.label')}</span>
          </span>
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>{MenuItems}</DropdownMenuSubContent>
      </DropdownMenuSub>

      <div className={'lg:hidden'}>
        <DropdownMenuLabel>{t('common:language.label')}</DropdownMenuLabel>

        {MenuItems}
      </div>
    </>
  );
}

function capitalize(lang: string) {
  return lang.slice(0, 1).toUpperCase() + lang.slice(1);
}
